import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, Ticket, Plus, Pencil, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CurrencyField, NumberField } from "@/components/ui/forms";
import { fmtDate } from "@/lib/datetime";

const th = "font-display font-bold px-4 py-3 text-xs tracking-wider uppercase text-left text-[#52525B]";
const fld = "h-11 rounded-none border-[#0A0A0A]";
const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));
const fmtFecha = (iso) => fmtDate(iso);

export default function Paquetes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // { id?, nombre, cantidad_folios, precio }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/paquetes");
      setRows(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setForm({ nombre: "", cantidad_folios: null, precio: null });
  const openEdit = (p) => setForm({ id: p.id_paquete, nombre: p.nombre, cantidad_folios: p.cantidad_folios, precio: Number(p.precio) });

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (!form.cantidad_folios || form.cantidad_folios <= 0) { toast.error("La cantidad de folios debe ser mayor a 0"); return; }
    if (!form.precio || form.precio <= 0) { toast.error("El precio debe ser mayor a 0"); return; }
    setSaving(true);
    try {
      const payload = { nombre: form.nombre.trim(), cantidad_folios: form.cantidad_folios, precio: Number(Number(form.precio).toFixed(2)) };
      if (form.id) await api.put(`/admin/paquetes/${form.id}`, payload);
      else await api.post("/admin/paquetes", payload);
      toast.success(form.id ? "Paquete actualizado" : "Paquete creado");
      setForm(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (p) => {
    try {
      await api.patch(`/admin/paquetes/${p.id_paquete}/estatus`, { estado: !p.estado });
      toast.success(p.estado ? "Paquete desactivado" : "Paquete activado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 flex items-center gap-2"><Ticket className="h-4 w-4" /> Catálogo</p>
            <h1 className="font-display text-4xl font-black tracking-tight">Paquetes de Folios</h1>
            <p className="text-sm text-[#52525B] mt-2">Define los paquetes a la venta. Los inactivos no se muestran a los tenants.</p>
          </div>
          <button onClick={openNew} data-testid="new-paquete-btn" className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Nuevo paquete</button>
        </div>

        <div className="bg-white border border-[#e4e4e7] overflow-x-auto">
          <table className="w-full text-sm" data-testid="paquetes-table">
            <thead><tr className="border-b border-[#e4e4e7]"><th className={th}>Nombre</th><th className={`${th} text-right`}>Folios</th><th className={`${th} text-right`}>Precio</th><th className={th}>Estatus</th><th className={th}>Creado</th><th className={`${th} text-right`}>Acciones</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="px-4 py-16 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-12 text-center text-[#52525B]" data-testid="paquetes-empty">Sin paquetes. Crea el primero.</td></tr>
              : rows.map((p) => (
                <tr key={p.id_paquete} className="border-b border-[#f0f0f1]" data-testid={`paquete-row-${p.id_paquete}`}>
                  <td className="px-4 py-3 font-semibold">{p.nombre}</td>
                  <td className="px-4 py-3 text-right font-mono">{p.cantidad_folios.toLocaleString("es-MX")}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{fmt(p.precio)}</td>
                  <td className="px-4 py-3"><span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${p.estado ? "text-[#047857] border-[#10B981]/40 bg-[#10B981]/5" : "text-[#B91C1C] border-[#EF4444]/40 bg-[#EF4444]/5"}`}>{p.estado ? "Activo" : "Inactivo"}</span></td>
                  <td className="px-4 py-3">{fmtFecha(p.fecha_creacion)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(p)} data-testid={`edit-paquete-${p.id_paquete}`} className="h-8 w-8 inline-flex items-center justify-center border border-[#e4e4e7] hover:border-[#0A0A0A] transition-colors duration-200"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => toggle(p)} data-testid={`toggle-paquete-${p.id_paquete}`} className={`h-8 w-8 inline-flex items-center justify-center border transition-colors duration-200 ${p.estado ? "border-[#e4e4e7] hover:border-[#B91C1C] text-[#B91C1C]" : "border-[#e4e4e7] hover:border-[#047857] text-[#047857]"}`}>{p.estado ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      <Dialog open={form !== null} onOpenChange={(o) => { if (!o) setForm(null); }}>
        <DialogContent className="rounded-none border-[#0A0A0A]" data-testid="paquete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight">{form?.id ? "Editar paquete" : "Nuevo paquete"}</DialogTitle>
            <DialogDescription>Define nombre, cantidad de folios y precio.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Nombre</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Paquete Básico" data-testid="paquete-nombre" className={fld} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Cantidad de folios</Label>
                  <NumberField value={form.cantidad_folios} onChange={(v) => setForm({ ...form, cantidad_folios: v })} min={1} placeholder="Ej. 500" data-testid="paquete-cantidad" className={fld} /></div>
                <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Precio</Label>
                  <CurrencyField value={form.precio} onChange={(v) => setForm({ ...form, precio: v })} data-testid="paquete-precio" className={fld} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setForm(null)} className="h-11 px-5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm">Cancelar</button>
            <button onClick={save} disabled={saving} data-testid="paquete-save" className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
